/**
 * The single place that talks to the edge.
 *
 * Everything goes through `request`, so auth headers, error shaping and JSON
 * handling exist once. The token is supplied by a getter rather than stored
 * here: with Clerk it is a short-lived JWT that must be re-fetched per call,
 * and with the dev provider it comes from local storage.
 */
import type { Encounter } from '@/api/combat';
import type {
  AdminAudit,
  AdminProviderHealth,
  AdminSessionSummary,
  AuthConfig,
  BillingState,
  Campaign,
  ChatMessage,
  ChatMode,
  Checkpoint,
  DevLoginResponse,
  Me,
  NpcLineCue,
  Session,
  SessionHistoryRow,
  SessionRecap,
  TranscriptTurn,
} from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** 402/403 mean "your account needs attention", not "something broke". */
  get isAccountIssue(): boolean {
    return this.status === 402 || this.status === 403;
  }

  get isAuthFailure(): boolean {
    return this.status === 401;
  }
}

type TokenGetter = () => Promise<string | null>;

let getToken: TokenGetter = async () => null;

export function setTokenGetter(fn: TokenGetter): void {
  getToken = fn;
}

/**
 * Turn a failed response into something a human can act on.
 *
 * The raw body is often a FastAPI validation blob or an HTML error page, and
 * showing either to a GM mid-session is useless.
 */
function describeFailure(status: number, body: unknown, path: string): string {
  const detail = extractDetail(body);

  // A malformed pass code is ordinary input feedback, not a server failure.
  // FastAPI's default was "String should have at least 8 characters", which
  // is implementation language and gives no clue what a real code looks like.
  if (path.startsWith('/api/invite/redeem')) {
    if (status === 422) {
      return 'That doesn’t look like a pass code. Pass codes start with TL-.';
    }
    if (typeof detail === 'string' && detail.includes('different email')) {
      return 'That pass code was issued to a different email address.';
    }
    if (status === 400) {
      return 'That pass code isn’t valid, or it has already been used.';
    }
  }

  if (typeof detail === 'string') {
    const known = knownDetail(detail);
    if (known) return known;
  }
  if (Array.isArray(detail)) {
    return describeValidation(detail);
  }

  // Status is safer than an arbitrary server string: some backend details
  // contain table names, configuration keys, or provider terminology.
  if (status === 401) return 'Your session expired. Sign in again.';
  if (status === 402) {
    return 'This workspace has reached its usage limit. Check Billing before starting another session.';
  }
  if (status === 403) return 'You don’t have permission to do that.';
  if (status === 404)
    return 'We couldn’t find that anymore. Refresh and try again.';
  if (status === 409) {
    return 'That changed before your request finished. Refresh and try again.';
  }
  if (status === 413)
    return 'That is too large to save. Shorten it and try again.';
  if (status === 422) return 'Please check what you entered and try again.';
  if (status === 429)
    return 'That was a little too fast. Wait a moment and try again.';
  if (status >= 500) {
    return 'Throughline hit a temporary problem. Please try again in a moment.';
  }
  return 'We couldn’t complete that. Please check what you entered and try again.';
}

type ValidationIssue = {
  loc?: Array<string | number>;
  msg?: string;
  type?: string;
  ctx?: Record<string, unknown>;
};

function extractDetail(body: unknown): unknown {
  if (body && typeof body === 'object' && 'detail' in body) {
    return (body as { detail: unknown }).detail;
  }
  return body;
}

function describeValidation(detail: unknown[]): string {
  const issue = detail[0] as ValidationIssue | undefined;
  const field = validationField(issue?.loc);
  const type = issue?.type ?? '';
  const message = issue?.msg?.toLowerCase() ?? '';
  if (type === 'missing' || message.includes('field required')) {
    return `Please enter ${field}.`;
  }
  if (type.includes('too_short') || message.includes('at least')) {
    const minimum = issue?.ctx?.min_length;
    return typeof minimum === 'number'
      ? `Please use at least ${minimum} characters for ${field}.`
      : `The ${field} is too short. Please check it and try again.`;
  }
  if (type.includes('too_long') || message.includes('at most')) {
    return `The ${field} is too long. Shorten it and try again.`;
  }
  return `Please check ${field} and try again.`;
}

function validationField(loc: ValidationIssue['loc']): string {
  const raw = [...(loc ?? [])]
    .reverse()
    .find(
      (part): part is string => typeof part === 'string' && part !== 'body',
    );
  const key = raw?.replaceAll('_', ' ') ?? 'that field';
  const names: Record<string, string> = {
    code: 'the pass code',
    content: 'a message',
    org: 'a workspace name',
    premise: 'the campaign details',
  };
  return names[key] ?? key;
}

function knownDetail(detail: string): string | null {
  const value = detail.trim().toLowerCase();
  if (
    value.includes('billing not configured') ||
    value.includes('stripe price')
  ) {
    return 'Billing is temporarily unavailable. Please try again later.';
  }
  if (value.includes('spending cap') || value.includes('usage limit')) {
    return 'This workspace has reached its usage limit. Check Billing before continuing.';
  }
  if (value === 'campaign not found') {
    return 'That campaign is no longer available. Refresh and try again.';
  }
  if (value.includes('session is live')) {
    return 'End the live session before doing that.';
  }
  if (value.includes('session is ') || value === 'live session not found') {
    return 'That session is no longer live. Refresh to see its current state.';
  }
  if (value === 'session not found') {
    return 'That session is no longer available. Refresh and try again.';
  }
  if (
    value === 'checkpoint not found' ||
    value === 'checkpoint not rejectable'
  ) {
    return 'That checkpoint can’t be changed now. Refresh to see the current board.';
  }
  if (value === 'encounter not found') {
    return 'That encounter is no longer available. Refresh and try again.';
  }
  if (value === 'battle map not found') {
    return 'That battle map is no longer available. Close it and open it again.';
  }
  if (value.includes('board changed before this save')) {
    return 'The board changed before your edit was saved. It has been refreshed; try the edit again.';
  }
  if (value === 'suggestion not found') {
    return 'That suggestion is no longer available. Refresh the board.';
  }
  if (value === 'player link not found' || value === 'that link is not valid') {
    return 'That player link is no longer valid. Create a new link for that player.';
  }
  if (value.includes('only move your own token')) {
    return 'You can only move your own token.';
  }
  if (value === 'token not on this map') {
    return 'That token is no longer on the map. The board will refresh.';
  }
  if (value.includes('fight has no map yet')) {
    return 'Build the encounter before opening its battle map.';
  }
  if (value.includes('fight is no longer waiting')) {
    return 'That encounter has already moved on. Refresh to see its current state.';
  }
  if (value.includes('npc line is no longer waiting')) {
    return 'That suggested line was already cleared.';
  }
  if (value === 'draft not found' || value === 'draft or campaign not found') {
    return 'That saved draft is no longer available. Refresh the draft list.';
  }
  if (value.includes('draft is too large')) {
    return 'That draft is too large to save. Shorten a few answers and try again.';
  }
  if (value === 'content is required')
    return 'Write a message before sending it.';
  if (value.includes('no conversation to save')) {
    return 'There isn’t a planning conversation to save yet.';
  }
  if (value.includes('account needs') && value.includes('email')) {
    return 'Add a primary email address to your account before continuing.';
  }
  if (value === 'this account has already used its trial') {
    return 'This account has already used its free trial.';
  }
  if (value === 'this account already has access') {
    return 'This account already has access.';
  }
  if (value.includes('slow down') || value.includes('too many')) {
    return 'That was a little too fast. Wait a moment and try again.';
  }
  if (value.includes('showrunner could not answer')) {
    return 'The Showrunner couldn’t answer just now. Please try again.';
  }
  if (value.includes('could not write the plan')) {
    return 'The plan couldn’t be saved just now. Please try again.';
  }
  return null;
}

/** Never expose arbitrary Error.message text in product UI. */
export function userFacingError(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  return error instanceof ApiError ? error.message : fallback;
}

export async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; signal?: AbortSignal } = {},
): Promise<T> {
  const { method = 'GET', body, signal } = options;
  const headers: Record<string, string> = {};
  if (body !== undefined && body !== null) {
    headers['Content-Type'] = 'application/json';
  }
  const token = await getToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      body:
        body === undefined || body === null ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') throw error;
    throw new ApiError(
      0,
      'We couldn’t reach Throughline. Check your connection and try again.',
      error,
    );
  }

  const text = await response.text();
  let parsed: unknown = text;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      /* leave as text — describeFailure handles it */
    }
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      describeFailure(response.status, parsed, path),
      parsed,
    );
  }
  return parsed as T;
}

// -- auth --------------------------------------------------------------------
export const getAuthConfig = () => request<AuthConfig>('/api/auth/config');

export const devLogin = (email: string, org: string) =>
  request<DevLoginResponse>('/api/auth/dev/login', {
    method: 'POST',
    body: { email, org },
  });

export const getMe = () => request<Me>('/api/auth/me');

// -- campaigns ---------------------------------------------------------------
export const listCampaigns = () => request<Campaign[]>('/api/campaigns');

export const createCampaign = (input: {
  slug: string;
  name: string;
  premise: string;
  system_key?: string;
  system_notes?: string;
  /** BCP-47 primary subtag. Drives transcription hints and the language
   *  the showrunner writes in. */
  language?: string;
  /** Saved as party_members, which is what encounter difficulty is scored
   *  against. Omit it and every fight is balanced for four level-3 characters. */
  players?: {
    name: string;
    player_name: string;
    level: number | null;
    signature: string;
  }[];
}) => request<Campaign>('/api/campaigns', { method: 'POST', body: input });

// -- campaign drafts ---------------------------------------------------------
/**
 * The durable copy of a setup draft.
 *
 * localStorage is still the fast path — it makes typing instant and survives a
 * refresh with no round trip. These outlive the browser, and unlike the old
 * single localStorage key there can be more than one.
 */
export interface CampaignDraftRow {
  id: string;
  name: string;
  draft_json: Record<string, unknown>;
  /** Set once this draft was used to create a campaign. The draft is kept. */
  created_campaign_id: string | null;
  created_campaign_name?: string | null;
  source_draft_id: string | null;
  created_at: string;
  updated_at: string;
}

export const listCampaignDrafts = () =>
  request<{ drafts: CampaignDraftRow[] }>('/api/campaign-drafts');

export const createCampaignDraft = (name: string, draft: unknown) =>
  request<CampaignDraftRow>('/api/campaign-drafts', {
    method: 'POST',
    body: { name, draft },
  });

export const saveCampaignDraft = (id: string, name: string, draft: unknown) =>
  request<CampaignDraftRow>(`/api/campaign-drafts/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: { name, draft },
  });

export const duplicateCampaignDraft = (id: string) =>
  request<CampaignDraftRow>(
    `/api/campaign-drafts/${encodeURIComponent(id)}/duplicate`,
    { method: 'POST' },
  );

/** Record that a draft became a campaign. Best-effort: the campaign exists
 *  either way, so a failure here is not worth telling the GM about. */
export const markDraftUsed = (draftId: string, campaignId: string) =>
  request<CampaignDraftRow>(
    `/api/campaign-drafts/${encodeURIComponent(draftId)}/created/${encodeURIComponent(campaignId)}`,
    { method: 'POST' },
  );

export const deleteCampaignDraft = (id: string) =>
  request<void>(`/api/campaign-drafts/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });

// -- glance ------------------------------------------------------------------
/** One card in the canon panel. `why` is the ranker's reasons, which the panel
 *  shows: "just named" and "connected to where you are" deserve different
 *  amounts of a GM's attention mid-scene. */
export interface GlanceEntity {
  id: string;
  kind: 'person' | 'place' | 'faction' | 'item' | 'thread' | string;
  name: string;
  one_line: string;
  open_thread: boolean;
  facts: { fact: string; source: string }[];
  why: string[];
  score: number;
}

export const getGlance = (sessionId: string, showing: string[] = []) =>
  request<{ generated_at: string; entities: GlanceEntity[] }>(
    `/api/sessions/${sessionId}/glance?showing=${encodeURIComponent(showing.join(','))}`,
  );

export interface PoolName {
  name: string;
  gender: 'masc' | 'fem' | 'neutral';
  age: 'young' | 'adult' | 'old';
  standing: 'poor' | 'common' | 'rich';
  note: string;
}

export const getNames = (campaignId: string) =>
  request<{
    status: 'pending' | 'ready' | 'failed';
    names: PoolName[];
    error: string | null;
  }>(`/api/campaigns/${campaignId}/names`);

export const markNameUsed = (campaignId: string, name: string) =>
  request<{ ok: true }>(`/api/campaigns/${campaignId}/names/used`, {
    method: 'POST',
    body: { name },
  });

export interface PlanMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
}

export const getPlan = (campaignId: string) =>
  request<{ messages: PlanMessage[]; saved_plan: string | null }>(
    `/api/campaigns/${campaignId}/plan`,
  );

export const sendPlanMessage = (campaignId: string, content: string) =>
  request<PlanMessage>(`/api/campaigns/${campaignId}/plan`, {
    method: 'POST',
    body: { content },
  });

export const savePlan = (campaignId: string) =>
  request<{ saved_plan: string }>(`/api/campaigns/${campaignId}/plan/save`, {
    method: 'POST',
  });

/** Finished or skipped the first-run tour. Both end it for good. */
export const markOnboarded = () =>
  request<{ onboarded: true }>('/api/auth/me/onboarded', { method: 'POST' });

// -- sessions ----------------------------------------------------------------
export const getActiveSession = () =>
  request<Session | null>('/api/sessions/active');

export const createSession = (campaignId: string, sessionIntent = '') =>
  request<Session>('/api/sessions', {
    method: 'POST',
    body: { campaign_id: campaignId, session_intent: sessionIntent },
  });

export const endSession = (sessionId: string) =>
  request<Session>(`/api/sessions/${sessionId}/end`, { method: 'POST' });

export const getStreamToken = (sessionId: string) =>
  request<{ token: string }>(`/api/sessions/${sessionId}/stream-token`, {
    method: 'POST',
  });

// -- checkpoints -------------------------------------------------------------
// These live under /api/sessions/{id}/... — the checkpoint and chat routers
// are mounted with a session-scoped prefix rather than taking session_id as a
// query parameter.
const forSession = (sessionId: string) =>
  `/api/sessions/${encodeURIComponent(sessionId)}`;

export const listCheckpoints = (sessionId: string) =>
  request<Checkpoint[]>(`${forSession(sessionId)}/checkpoints`);

/**
 * The encounter prepared for this session, if the agent has built one.
 *
 * Returns `{ encounter: null }` rather than 404 when nothing is waiting —
 * "no fight is coming" is the normal state for most of a session.
 */
export const getSessionEncounter = (sessionId: string) =>
  request<{ encounter: Encounter | null }>(
    `${forSession(sessionId)}/encounter`,
  );

/**
 * Why the GM refused a fight. Three noes wearing one button, and two of them
 * want the opposite response from the system: `wrong_fight` carries a
 * redirect the agent builds immediately, while the other two tell the
 * speculative watcher to stop offering.
 */
export type DiscardKind = 'wrong_fight' | 'not_now' | 'no_combat';

/** Move an encounter through running -> resolved, or throw it away. */
export const setEncounterStatus = (
  encounterId: string,
  status: 'building' | 'running' | 'resolved' | 'discarded',
  refusal?: { kind?: DiscardKind; note?: string },
) =>
  request<{ encounter: Encounter }>(`/api/encounters/${encounterId}/status`, {
    method: 'POST',
    // A plain object. request() stringifies — passing a string here encoded it
    // twice, so the server received a JSON *string* where it wanted an object,
    // answered 422, and useEncounter's catch swallowed it. The button did
    // nothing, silently, for every status change: "Not this" and "Fight over"
    // never fired, and "Open" only looked like it worked because the board is
    // opened locally before this call is made.
    body: { status, ...(refusal ?? {}) },
  });

export const rejectCheckpoint = (
  sessionId: string,
  checkpointId: string,
  redirect: string,
) =>
  request<unknown>(
    `${forSession(sessionId)}/checkpoints/${encodeURIComponent(checkpointId)}/reject`,
    { method: 'POST', body: { redirect } },
  );

export const advance = (sessionId: string) =>
  request<unknown>(`${forSession(sessionId)}/advance`, { method: 'POST' });

export interface CampaignSynthesis {
  id: string;
  status: 'pending' | 'complete' | 'failed';
  summary_md: string;
  images: { url: string; caption: string }[];
  error: string | null;
  cost_cents: number;
  created_at: string;
  generated_at: string | null;
}

/** "Make it make sense" — the latest run, or null if never pressed. */
export const getSynthesis = (campaignId: string) =>
  request<{ synthesis: CampaignSynthesis | null }>(
    `/api/campaigns/${campaignId}/synthesis`,
  );

export const startSynthesis = (campaignId: string) =>
  request<{ id: string; already_running: boolean }>(
    `/api/campaigns/${campaignId}/synthesis`,
    { method: 'POST' },
  );

// -- chat --------------------------------------------------------------------
export const listChatMessages = (sessionId: string) =>
  request<ChatMessage[]>(`${forSession(sessionId)}/chat`);

export const sendChatMessage = async (
  sessionId: string,
  mode: ChatMode,
  content: string,
): Promise<ChatMessage> => {
  const { message } = await request<{
    message: Pick<ChatMessage, 'id' | 'mode' | 'role' | 'content'>;
  }>(`${forSession(sessionId)}/chat`, {
    method: 'POST',
    body: { mode, content },
  });
  return { ...message, session_id: sessionId, created_at: null };
};

// -- prepared NPC lines -----------------------------------------------------
export const listNpcLines = (sessionId: string) =>
  request<NpcLineCue[]>(`${forSession(sessionId)}/npc-lines`);

export const resolveNpcLine = (
  sessionId: string,
  cueId: string,
  status: 'used' | 'dismissed',
) =>
  request<NpcLineCue>(
    `${forSession(sessionId)}/npc-lines/${encodeURIComponent(cueId)}`,
    { method: 'PATCH', body: { status } },
  );

// -- history -----------------------------------------------------------------
/**
 * Past sessions, newest first. `before` is the started_at of the oldest row
 * already held, so paging cannot skip or repeat a session when one is running
 * and the list shifts under it (an offset would).
 */
export const listSessionHistory = (before?: string, limit = 50) =>
  request<{ sessions: SessionHistoryRow[]; has_more: boolean }>(
    `/api/sessions/history?limit=${limit}` +
      (before ? `&before=${encodeURIComponent(before)}` : ''),
  );

/** The raw transcript of a session, for reading a night back. */
export const listTranscript = (sessionId: string, maxChunks = 500) =>
  request<TranscriptTurn[]>(
    `${forSession(sessionId)}/transcript?max_chunks=${maxChunks}`,
  );

// -- campaign reference ------------------------------------------------------
/**
 * Everything the GM told the system, for the reference panel: their setup
 * answers, the party as stored, the house rules, tonight's intent, and the
 * opening narration. One fetch, because this opens mid-session.
 */
export interface SessionSetup {
  campaign_id: string;
  slug: string;
  name: string;
  /** The compiled setup blob the agent receives as <PREMISE>. */
  premise: string;
  system_key: string | null;
  system_notes: string | null;
  session_intent: string;
  started_at: string | null;
  opening_narration: string | null;
  /** As stored in party_members — the numbers encounter difficulty is scored
   *  against, which is not always what the GM thinks they typed. */
  party: {
    name: string;
    player_name: string;
    level: number | null;
    source: string;
  }[];
}

export const getSessionSetup = (sessionId: string) =>
  request<SessionSetup>(`${forSession(sessionId)}/setup`);

// -- recap -------------------------------------------------------------------
export const getSessionRecap = (sessionId: string) =>
  request<SessionRecap>(`${forSession(sessionId)}/recap`);

export const saveGmNotes = (sessionId: string, gmNotes: string) =>
  request<SessionRecap>(`${forSession(sessionId)}/recap`, {
    method: 'PATCH',
    body: { gm_notes: gmNotes },
  });

// -- access ------------------------------------------------------------------
/** Redeem a guest pass. Activates the tenant with a comped cap, no payment. */
export const redeemInvite = (code: string) =>
  request<{ ok: boolean }>('/api/invite/redeem', {
    method: 'POST',
    body: { code },
  });

/** Take the self-serve trial. No code, no email, nobody to wait for. */
export const startTrial = () =>
  request<{ ok: boolean; granted_cents: number }>('/api/invite/trial', {
    method: 'POST',
  });

// -- billing -----------------------------------------------------------------
export const getBillingState = () =>
  request<BillingState>('/api/billing/state');

export const startCheckout = (plan: string) =>
  request<{ url: string }>('/api/billing/checkout', {
    method: 'POST',
    body: { plan },
  });

export const openBillingPortal = () =>
  request<{ url: string }>('/api/billing/portal', { method: 'POST' });

// -- admin -------------------------------------------------------------------
export const listAdminSessions = (query = '') =>
  request<{ sessions: AdminSessionSummary[] }>(
    `/api/admin/sessions${query ? `?q=${encodeURIComponent(query)}` : ''}`,
  );

export const getAdminAudit = (sessionId: string) =>
  request<AdminAudit>(
    `/api/admin/sessions/${encodeURIComponent(sessionId)}/audit`,
  );

export const getAdminProviderHealth = () =>
  request<AdminProviderHealth>('/api/admin/provider-health');

// -- telemetry ---------------------------------------------------------------
export function recordClientEvent(
  eventType: string,
  level: 'debug' | 'info' | 'warn' | 'error' = 'info',
  message = '',
  payload: Record<string, unknown> = {},
  sessionId: string | null = null,
): void {
  // Telemetry must never break the page or surface an error to the GM.
  void request('/api/client-events', {
    method: 'POST',
    body: {
      event_type: eventType,
      level,
      message: message.slice(0, 2000),
      source: 'dashboard',
      url: window.location.href,
      session_id: sessionId ?? undefined,
      payload,
    },
  }).catch(() => undefined);
}
